import { z } from 'zod';

import { BusDaySchema } from '../bus_planner/bus_planner_schema.js';
import {
  BusSeatLegSelectionSchema,
  type BusSeatLegSelection,
} from '../bus_seat_map/bus_seat_map_schema.js';
import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { CHAT_UI_MAX_OFFERS } from '../../shared/schema/limits.js';
import { chatUiBase } from '../../shared/schema/base.js';

// Veredito de conformidade da viagem de ônibus com a política corporativa, por
// oferta (produtor agents, determinístico — derivado de `policies.within`).
// `status:'out'` quando ALGUMA regra reprova — é o agregado que guia o selo e a
// exigência de justificativa. `rules` lista as regras avaliadas com status
// próprio e, quando há descritivo, o `text` rico. Espelha o shape de política do
// flight (`ChatUiFlightPolicy{,Rule}Schema`). Ausente → oferta sem dado de
// política (ex.: stub sem veredito) degrada sem selo.
const ChatUiBusPolicyRuleSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  // ok = regra cumprida; violated = reprovada; na = não se aplica/não avaliada.
  status: z.enum(['ok', 'violated', 'na']),
  // Prosa rica do descritivo. Ausente (fail-soft) → só título + status.
  text: z.string().optional(),
});
export type ChatUiBusPolicyRule = z.infer<typeof ChatUiBusPolicyRuleSchema>;
const ChatUiBusPolicySchema = z.object({
  status: z.enum(['in', 'out']),
  rules: z.array(ChatUiBusPolicyRuleSchema).optional(),
});
export type ChatUiBusPolicy = z.infer<typeof ChatUiBusPolicySchema>;

/** Parada (rodoviária) de embarque/desembarque de uma viagem de ônibus.
 *  Exportado porque o `product_checkout` (bus item detail) reusa a mesma forma
 *  de parada — fonte única, espelhando como o flight reusa `ChatUiFlightSegment`. */
export const ChatUiBusStopSchema = z.object({
  // Nome da rodoviária/parada (ex.: "São Paulo - Tietê").
  stopName: z.string().min(1),
  // Local-naive (sem Z), como as ofertas de voo: horário da parada sem fuso.
  dateTime: z.string().min(1),
});
export type ChatUiBusStop = z.infer<typeof ChatUiBusStopSchema>;

/** A baldeação de uma viagem com conexão: a parada e, quando o fornecedor
 *  informa, a chegada e a partida nela (local-naive, como `ChatUiBusStop`). Sem
 *  os horários, a página só nomeia a parada. */
export const ChatUiBusConnectionSchema = z.object({
  stopName: z.string().min(1),
  arrivalDateTime: z.string().min(1).optional(),
  departureDateTime: z.string().min(1).optional(),
});
export type ChatUiBusConnection = z.infer<typeof ChatUiBusConnectionSchema>;

export const ChatUiBusOfferSchema = z.object({
  offerId: z.string().min(1),
  departure: ChatUiBusStopSchema,
  arrival: ChatUiBusStopSchema,
  // Duração total da viagem em minutos (fonte autoritativa do provider).
  durationMinutes: z.number().int().min(0),
  // Empresa/viação operadora (ex.: "Cometa").
  carrierName: z.string().min(1),
  // Código da viação na viagem (`travelCompany.externalCode` da busca) — um dos
  // dois jeitos de a viação preferida do viajante casar com a viagem; o outro é
  // o nome. Opcional.
  carrierCode: z.string().min(1).optional(),
  // URL do logo da viação (`travelCompany.logo.url`). Opcional → degrada para o
  // ícone genérico de ônibus.
  logoUrl: z.string().min(1).optional(),
  // Descrição da classe de poltrona (ex.: "Leito", "Executivo").
  seatClass: z.string().min(1),
  // Assentos disponíveis na viagem.
  availableSeats: z.number().int().min(0),
  price: ChatUiMoneySchema,
  // Número de conexões (0 = direto).
  connections: z.number().int().min(0),
  // Onde e quando é a conexão (opcional → degrada para só "Com conexão").
  connection: ChatUiBusConnectionSchema.optional(),
  // Conformidade com a política corporativa (opcional → degrada sem selo).
  policy: ChatUiBusPolicySchema.optional(),
  // Por que a viagem está na seleção curta do card (curadoria determinística
  // do agente): a recomendada, a de menor preço (`cheaperBy` a menos que a
  // recomendada, por pessoa) ou a de menos tempo (`fasterByMinutes`). O card
  // escreve o selo e a nota a partir destes FATOS — cada selo exige o SEU
  // número, para a nota nunca dizer "R$ 0,00 a menos". Ausente ⇒ sem selo.
  highlight: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('recommended') }).strict(),
      z.object({ kind: z.literal('cheapest'), cheaperBy: z.number().nonnegative() }).strict(),
      z
        .object({ kind: z.literal('fastest'), fasterByMinutes: z.number().int().nonnegative() })
        .strict(),
    ])
    .optional(),
});
export type ChatUiBusOffer = z.infer<typeof ChatUiBusOfferSchema>;

/** O teto de viajantes de uma busca (o da bancada). */
const BUS_MAX_OFFER_TRAVELERS = 9;

/** Cap da lista completa (mesmo papel do `CHAT_UI_MAX_ALL_OFFERS` do flight, que
 *  subiu para 100 por medição de busca de voo; ônibus segue em 30): maior que o
 *  conjunto curado, ainda limita o tamanho do frame SSE em buscas largas. */
export const CHAT_UI_MAX_ALL_BUS_OFFERS = 30;

/** Membro `bus_offers` do `ChatUiSchema` (discriminatedUnion). */
export const BusOffersBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('bus_offers'),
    offers: z.array(ChatUiBusOfferSchema).min(1).max(CHAT_UI_MAX_OFFERS),
    // Lista completa (sem curadoria). Opcional: ausente ⇒ só `offers` (degrade).
    allOffers: z.array(ChatUiBusOfferSchema).max(CHAT_UI_MAX_ALL_BUS_OFFERS).optional(),
    // Perna do ida-e-volta (rótulo). `outbound` = IDA; `return` = VOLTA. Ausente
    // = só ida (one-way) — único caso suportado nesta etapa.
    leg: z.enum(['outbound', 'return']).optional(),
    // As cidades da perna como a pessoa pediu (ida: origem → destino; volta, o
    // contrário) — a oferta só nomeia a rodoviária. Ausente (blocos antigos) ⇒
    // o card cai no nome da rodoviária.
    route: z
      .object({ from: z.string().min(1), to: z.string().min(1) })
      .strict()
      .optional(),
    // Presente ⇒ o card é a escolha VIVA: `Selecionar` abre a planta da viagem
    // e a confirmação volta por `interaction` (este challenge + a
    // `BusOfferSeatsSelection`). Ausente (blocos antigos) ⇒ o pick é o
    // quick-reply de texto.
    challengeId: z.string().min(1).optional(),
    // A viagem que abre já na escolha de poltrona — a escolha por TEXTO ("a
    // segunda") reapresenta o card com ela aberta.
    autoExpand: z.string().min(1).optional(),
    // Ida e volta: o card desenha as duas pernas no stepper.
    roundTrip: z.boolean().optional(),
    // No card da VOLTA: a ida já fechada, para o passo concluído do stepper —
    // a viação (logo), a classe e as poltronas de cada trecho (frame 06). Os
    // três últimos são opcionais: blocos antigos só trazem horários e viação.
    outboundPick: z
      .object({
        departure: ChatUiBusStopSchema,
        arrival: ChatUiBusStopSchema,
        carrierName: z.string().min(1),
        logoUrl: z.string().min(1).optional(),
        seatClass: z.string().min(1).optional(),
        seats: z.array(z.string().min(1)).max(2).optional(),
      })
      .strict()
      .optional(),
    // O dia buscado da perna em cena: com o `challengeId`, o card desenha a
    // faixa de datas (frame 04) e cada dia volta ao agente como uma busca nova
    // (`BusOfferDaySelection`). `dayBounds` limita a faixa: a ida não passa da
    // volta, e a volta não vem antes da ida.
    day: BusDaySchema.optional(),
    dayBounds: z
      .object({ min: BusDaySchema.optional(), max: BusDaySchema.optional() })
      .strict()
      .optional(),
    // Quem viaja (o próprio viajante na conversa) — o nome nas iniciais da
    // poltrona escolhida. Bloco de um viajante só; com vários, `travelers`.
    traveler: z
      .object({ name: z.string().min(1) })
      .strict()
      .optional(),
    // Quem viaja, quando a busca levou vários (#5472): cada um escolhe a sua
    // poltrona em cada trecho, e a escolha volta por id (`BusOfferSegmentSelection`).
    travelers: z
      .array(z.object({ id: z.string().min(1), name: z.string().min(1) }).strict())
      .min(2)
      .max(BUS_MAX_OFFER_TRAVELERS)
      .optional(),
  })
  .strict();

/** A resolução de UM trecho no card de partidas: a poltrona de quem viaja só
 *  (`seatCode`), "seguir sem assento" (`noSeat`) ou, com vários viajantes
 *  (`travelers` do bloco), a poltrona de cada um por id (`seats`, a forma da
 *  bancada). */
export const BusOfferSegmentSelectionSchema = z.union([
  BusSeatLegSelectionSchema,
  z
    .object({
      seats: z.record(z.string().min(1), z.string().min(1)).refine((seats) => {
        const size = Object.keys(seats).length;
        return size >= 1 && size <= BUS_MAX_OFFER_TRAVELERS;
      }),
    })
    .strict(),
]);
export type BusOfferSegmentSelection =
  | BusSeatLegSelection
  | { seats: Record<string, string>; seatCode?: never; noSeat?: never };

/**
 * A escolha do card de partidas, ecoada em `AppChannelInboundRequest.confirmation.
 * selections` (+ o `challengeId` do card): a viagem e a resolução de CADA trecho
 * dela, na ordem da viagem (1 = direto ou 1º trecho; 2 = depois da conexão) —
 * uma poltrona (código que a planta ofereceu, uma por viajante) ou "seguir sem
 * assento". Nunca texto livre do LLM (anti-injection).
 */
export const BusOfferSeatsSelectionSchema = z
  .object({
    offerId: z.string().min(1),
    segments: z.array(BusOfferSegmentSelectionSchema).min(1).max(2),
  })
  .strict();
export type BusOfferSeatsSelection = {
  offerId: string;
  segments: BusOfferSegmentSelection[];
};

/**
 * Um dia da faixa de datas do card de partidas, ecoado em `confirmation.
 * selections` (+ o `challengeId` do card): a perna em cena buscada de novo
 * nesse dia. Trocar o dia é pedir ao agente — o card não filtra nada.
 */
export const BusOfferDaySelectionSchema = z.object({ day: BusDaySchema }).strict();
export type BusOfferDaySelection = z.infer<typeof BusOfferDaySelectionSchema>;
